import React from "react";
import { Link } from "react-router-dom";
import * as HoverCard from "@radix-ui/react-hover-card";

import cn from "@/utils/cn";

import { MainNavChildConfig } from "../config/mainNavConfig";

type FlyoutItem = {
  child: MainNavChildConfig;
  label: string;
  isActive: boolean;
};

type Props = {
  to: string;
  name: string;
  icon: React.ReactElement;
  isCurrent: boolean;
  isSectionActive?: boolean;
  flyoutItems: FlyoutItem[];
};

const SidebarNavCollapsedFlyoutItem = ({
  child,
  label,
  isActive,
}: FlyoutItem) => {
  const iconElement = child.icon ? <child.icon /> : null;

  return (
    <div className="flex items-center px-1.5 py-px">
      <Link
        to={child.to}
        className={cn(
          "flex flex-1  items-center gap-1.5 rounded-md px-2.5 pb-2.5 pt-2 transition-colors hover:bg-chorus-black-200",
          isActive
            ? "bg-chorus-black-200 text-chorus-gray-300"
            : "text-chorus-gray-500",
        )}
        data-testid={label}
      >
        {iconElement &&
          React.cloneElement(iconElement, {
            className: cn(
              "size-6 shrink-0",
              isActive ? "fill-chorus-gray-400" : "fill-chorus-gray-600",
            ),
          })}
        <span
          className={cn(
            "text-sm font-medium leading-5 tracking-[0.28px]",
            isActive ? "text-chorus-gray-300" : "text-chorus-gray-50",
          )}
        >
          {label}
        </span>
      </Link>
    </div>
  );
};

export const SidebarNavCollapsedFlyout = ({
  to,
  name,
  icon,
  isCurrent,
  isSectionActive = false,
  flyoutItems,
}: Props) => {
  const isActiveSection = isCurrent || isSectionActive;

  return (
    <HoverCard.Root openDelay={50} closeDelay={100}>
      <HoverCard.Trigger asChild>
        <Link
          to={to}
          className={cn(
            "group relative flex min-h-[64px] flex-col items-center justify-center gap-y-1 rounded-md px-1 py-2 text-md transition-all duration-300 hover:bg-chorus-black-200",
            isCurrent && "bg-chorus-black-200",
            isActiveSection
              ? "font-semibold text-chorus-gray-200"
              : "font-medium text-chorus-gray-300",
          )}
          data-testid={name}
        >
          <div className="relative">
            {React.cloneElement(icon, {
              className: cn(
                "shrink-0 transition-all duration-300",
                isActiveSection
                  ? "fill-chorus-gray-400"
                  : "fill-chorus-gray-600",
              ),
            })}
          </div>
          <span className="w-full break-words px-0.5 text-center text-xxs leading-4 transition-all duration-300">
            {name}
          </span>
        </Link>
      </HoverCard.Trigger>
      <HoverCard.Portal>
        <HoverCard.Content
          side="right"
          align="start"
          sideOffset={8}
          className="z-[9999] flex w-60 flex-col gap-0.5 rounded-lg border border-chorus-black-100 bg-chorus-black-300 py-2 shadow-[0px_6px_8px_-4px_rgba(32,34,57,0.02),0px_12px_20px_-2px_rgba(32,34,57,0.04)]"
        >
          {flyoutItems.map((item) => (
            <SidebarNavCollapsedFlyoutItem key={item.child.id} {...item} />
          ))}
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
};
